import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

import { authApi } from '@/api/auth'
import { isConfirmedAuthFailure } from '@/api/authFailure'
import { apiRuntime } from '@/api/runtime'
import type { AuthResponse, TenantDuty, TenantMembershipRole, TenantUser } from '@/api/types'
import { queryClient } from '@/queryClient'

class TenantAccessRequiredError extends Error {}

export const useAuthStore = defineStore('tenant-auth', () => {
  const user = shallowRef<TenantUser | null>(null)
  const tenantId = shallowRef<number | null>(null)
  const membershipRole = shallowRef<TenantMembershipRole | null>(null)
  const duties = shallowRef<TenantDuty[]>([])
  const permissions = shallowRef<string[]>([])
  const initialized = shallowRef(false)

  const authenticated = computed(() => user.value !== null && tenantId.value !== null)
  const hasTenantAccess = computed(() => permissions.value.includes('tenant.profile.view'))
  const isOwner = computed(() => membershipRole.value === 'owner')
  const canShip = computed(() => permissions.value.includes('tenant.declaration.view'))
  const canReceive = computed(() => permissions.value.includes('tenant.receiving.assigned.view'))
  const canFinance = computed(() => permissions.value.includes('tenant.payment.view'))
  const canUseWarehouseWork = computed(() => permissions.value.includes('tenant.wms.task.claim'))
  const canSelfOperate = computed(() => permissions.value.includes('tenant.wms.self_operate.view'))

  const hasAnyPermission = (required: string[]) =>
    required.some((permission) => permissions.value.includes(permission))

  function applyAuth(auth: AuthResponse): void {
    if (!auth.tenantId || !auth.membershipRole || !auth.permissions.includes('tenant.profile.view')) {
      clear()
      throw new TenantAccessRequiredError('tenant_access_required')
    }
    user.value = auth.user
    tenantId.value = auth.tenantId
    membershipRole.value = auth.membershipRole
    duties.value = auth.duties
    permissions.value = auth.permissions
    apiRuntime.setAccessToken(auth.accessToken)
    initialized.value = true
  }

  async function login(payload: { identifier: string; password: string; captchaPayload?: string }) {
    applyAuth(await authApi.login(payload))
  }

  async function acceptInvitation(payload: Record<string, unknown>) {
    applyAuth(await authApi.acceptInvitation(payload))
  }

  async function ensureSession(): Promise<boolean> {
    if (authenticated.value) return hasTenantAccess.value
    if (initialized.value) return false
    try {
      applyAuth(await authApi.session())
      return true
    } catch (error) {
      if (error instanceof TenantAccessRequiredError) return false
      if (isConfirmedAuthFailure(error)) {
        clear()
        return false
      }
      throw error
    }
  }

  async function refreshSession(): Promise<boolean> {
    try {
      applyAuth(await authApi.session())
      return true
    } catch (error) {
      if (isConfirmedAuthFailure(error)) {
        clear()
        return false
      }
      throw error
    }
  }

  function clear(): void {
    user.value = null
    tenantId.value = null
    membershipRole.value = null
    duties.value = []
    permissions.value = []
    initialized.value = true
    apiRuntime.setAccessToken(null)
    queryClient.clear()
  }

  async function logout(): Promise<void> {
    try { await authApi.logout() } finally { clear() }
  }

  return {
    user,
    tenantId,
    membershipRole,
    duties,
    permissions,
    initialized,
    authenticated,
    hasTenantAccess,
    hasAnyPermission,
    isOwner,
    canShip,
    canReceive,
    canFinance,
    canUseWarehouseWork,
    canSelfOperate,
    applyAuth,
    login,
    acceptInvitation,
    ensureSession,
    refreshSession,
    clear,
    logout,
  }
})
